import { Ionicons } from '@expo/vector-icons';

import { MESSAGE_STATUS_LABEL, type MessageStatus } from '@/utils/inbox-triage';

/**
 * How each message state looks and reads, in one place.
 *
 * The pane banner, the list badge and the rail row all render the same four states. Three
 * independent spellings of "Waiting on customer" and three different icons for spam is how
 * one feature starts looking like three, so the vocabulary lives here — beside
 * `MESSAGE_STATUS_LABEL`, which owns the words for the same reason.
 */

export const STATUS_ICON: Record<MessageStatus, keyof typeof Ionicons.glyphMap> = {
	open: 'mail-outline',
	waiting: 'time-outline',
	spam: 'ban-outline',
	archived: 'archive',
};

/** Badge background per state. Deliberately muted — a state is not an alarm. */
export const STATUS_BADGE_BG: Record<Exclude<MessageStatus, 'open'>, string> = {
	// Matches ARCHIVED_BADGE_BG in message-list.tsx, which predates this file.
	archived: '#5F6368',
	waiting: '#6B4FA8',
	spam: '#8A2F2F',
};

/** What each away-state MEANS, since the word alone does not say what it does to the inbox. */
const STATUS_EXPLANATION: Record<Exclude<MessageStatus, 'open'>, string> = {
	archived: 'hidden from the inbox and every count',
	waiting: 'out of the queue until they write back',
	spam: 'kept as a record, out of the inbox',
};

/**
 * "Archived by Jane Smith — Sep 14, 2:15 PM" (#534).
 *
 * The actor is appended only when known. `statusBy` is NULL for every row archived before
 * migration 261 and for anything inbound mail moved by itself, and "Archived by unknown" is
 * a worse answer than "Archived", because it reads like the record was lost rather than
 * never taken.
 *
 * Viewer-local time on purpose. `statusAt` is a real moment — somebody clicked a button —
 * not a business date, so it is one of the cases the repo's date rules say should NOT go
 * through the Eastern-anchored helpers.
 */
export function describeMessageStatus(message: {
	status?: MessageStatus;
	statusAt?: string | null;
	statusByName?: string;
}): string {
	const status = message.status ?? 'open';
	if (status === 'open') return MESSAGE_STATUS_LABEL.open;

	const parts = [MESSAGE_STATUS_LABEL[status]];
	if (message.statusByName) parts.push(`by ${message.statusByName}`);

	const when = message.statusAt ? new Date(message.statusAt) : null;
	const stamp = when && !Number.isNaN(when.getTime())
		? when.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
		: null;

	const who = parts.join(' ');
	return stamp
		? `${who} — ${stamp} · ${STATUS_EXPLANATION[status]}`
		: `${who} — ${STATUS_EXPLANATION[status]}`;
}
